/**
 * Shareable-link onboarding wizard.
 *
 * Mounted at /onboard/link/:token. Renders the SAME wizard components as
 * the personal-invite flow (Welcome → Contact → Vehicle → Review), with
 * three deltas:
 *
 *   1. Source token type: shared-link (multi-use) instead of personal
 *      invite (single-use).
 *   2. No draft autosave or documents step (per spec — many people open
 *      the same URL; one tab's draft must not leak into another).
 *   3. Optional client branding: when the link has a client attached the
 *      brand bar swaps to client logo / name + small "Powered by
 *      GreenCharge" footer.
 *
 * Everything else — step shell, stepper, Back/Next controls, terminal
 * screens, validation summary — comes from onboardingWizard.jsx so the
 * two flows can never visually drift.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadSharedLink, submitSharedOnboarding } from '../../api/onboardingApi.js';
import { normalizePhone } from '../../utils/phone.js';
import { EMPTY_PAYLOAD, errMessage } from './onboardingShared.js';
import {
  StepWelcome,
  StepContact,
  StepVehicle,
  StepReview,
  TerminalScreen,
  WizardChrome,
} from './onboardingWizard.jsx';
import './OnboardingFlow.less';

const STEPS = [
  { id: 'welcome', label: 'Welcome', number: 1 },
  { id: 'contact', label: 'Contact', number: 2 },
  { id: 'vehicle', label: 'Vehicle', number: 3 },
  { id: 'review',  label: 'Review',  number: 4 },
];

const TERMINAL_COPY = {
  link_expired:   { title: 'This link has expired',           message: 'Contact the person who shared this link to get a fresh one.',     kind: 'warn' },
  link_disabled:  { title: 'This link has been disabled',     message: 'Contact the person who shared this link.',                       kind: 'warn' },
  link_archived:  { title: 'This link has been archived',     message: 'Contact the person who shared this link.',                       kind: 'warn' },
  link_full:      { title: 'This link has reached its limit', message: 'Submissions for this link are closed. Contact the person who shared it for next steps.', kind: 'warn' },
  not_found:      { title: 'Link not found',                  message: 'This onboarding link does not exist. Check the URL or contact the person who shared it.', kind: 'info' },
  internal_error: { title: 'Something went wrong',            message: 'Please try again in a moment.',                                  kind: 'warn' },
};

function SharedOnboardingFlow() {
  const { token: rawToken } = useParams();
  const token = decodeURIComponent(rawToken || '');

  const [phase, setPhase] = useState('loading'); // loading | form | submitted | approved | terminal
  const [terminalKind, setTerminalKind] = useState(null);
  const [link, setLink] = useState(null);
  const [branding, setBranding] = useState(null);
  const [payload, setPayload] = useState(EMPTY_PAYLOAD);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const dirtyRef = useRef(false);

  const currentStep = STEPS[step - 1] || STEPS[0];

  // Hydrate
  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    loadSharedLink(token)
      .then((data) => {
        if (cancelled) return;
        if (data?.ok) {
          setLink(data.link);
          setBranding(data.branding || null);
          setPhase('form');
        } else {
          setTerminalKind(data?.error || 'internal_error');
          setPhase('terminal');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.data?.error;
        if (code && TERMINAL_COPY[code]) {
          setTerminalKind(code);
        } else {
          setTerminalKind('internal_error');
        }
        // Surface the inferred message via errMessage on the terminal copy.
        setPhase('terminal');
      });
    return () => { cancelled = true; };
  }, [token]);

  // ── Field updates ────────────────────────────────────────────────
  const updateField = useCallback((block, field, value) => {
    setPayload((p) => ({ ...p, [block]: { ...p[block], [field]: value } }));
    dirtyRef.current = true;
  }, []);

  const setAgreedToTerms = useCallback((checked) => {
    setPayload((p) => ({ ...p, agreedToTerms: !!checked }));
    setValidationErrors((errs) => errs.filter((e) => e.key !== 'agreedToTerms'));
  }, []);

  const goNext = useCallback(() => setStep((s) => Math.min(STEPS.length, s + 1)), []);
  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);
  const jumpToStep = useCallback((stepId) => {
    const idx = STEPS.findIndex((s) => s.id === stepId);
    if (idx >= 0) setStep(idx + 1);
  }, []);

  // ── Submit ───────────────────────────────────────────────────────
  const computeMissingFields = useCallback(() => {
    const errs = [];
    if (!payload.contact.firstName?.trim()) errs.push({ key: 'firstName', label: 'First name', stepId: 'contact' });
    if (!payload.contact.lastName?.trim())  errs.push({ key: 'lastName',  label: 'Last name',  stepId: 'contact' });
    if (!payload.contact.email?.trim())     errs.push({ key: 'email',     label: 'Email',      stepId: 'contact' });
    // Phone stays optional; a number that was entered must be a valid one.
    if (payload.contact.phone?.trim() && !normalizePhone(payload.contact.phone).ok) errs.push({ key: 'phone', label: 'Phone (enter a valid number)', stepId: 'contact' });
    if (!payload.vehicle.plate?.trim())     errs.push({ key: 'plate',     label: 'Licence plate', stepId: 'vehicle' });
    if (!payload.agreedToTerms)             errs.push({ key: 'agreedToTerms', label: 'Declaration confirmation', stepId: 'review' });
    return errs;
  }, [payload]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    const missing = computeMissingFields();
    setValidationErrors(missing);
    setSubmitError(null);
    if (missing.length > 0) return;

    setSubmitting(true);
    try {
      const data = await submitSharedOnboarding(token, payload);
      if (data?.ok) {
        setPhase(data.autoApproved ? 'approved' : 'submitted');
      } else {
        setSubmitError(data?.message || 'Could not submit');
        setSubmitting(false);
      }
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code && TERMINAL_COPY[code]) {
        // The link terminal-flipped while the invitee was filling out the
        // form. Surface the matching terminal screen.
        setTerminalKind(code);
        setPhase('terminal');
        return;
      }
      if (code === 'rate_limited') {
        setSubmitError('Too many submissions from your address. Please wait a few minutes and try again.');
      } else {
        setSubmitError(errMessage(err, 'Could not submit'));
      }
      setSubmitting(false);
    }
  }, [submitting, computeMissingFields, token, payload]);

  // ── Header copy ──────────────────────────────────────────────────
  // Per spec: when the link has a client, the H1 + welcome step say
  // "Complete your onboarding for {clientName}".
  const clientName = branding?.clientName || null;
  const welcomeTitle = clientName
    ? `Welcome — let's onboard you for ${clientName}`
    : 'Welcome to GreenCharge';
  const headerTitle = step === 1
    ? (clientName ? `Complete your onboarding for ${clientName}` : 'Welcome')
    : `Step ${step} of ${STEPS.length}`;
  const headerSubtitle = step === 1 && link?.title && !clientName
    ? link.title
    : null;

  // ── Render ───────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="ob-shell">
        <div className="ob-container">
          <div className="ob-card">Loading…</div>
        </div>
      </div>
    );
  }

  if (phase === 'terminal') {
    const copy = TERMINAL_COPY[terminalKind] || TERMINAL_COPY.internal_error;
    return (
      <TerminalScreen
        title={copy.title}
        message={copy.message}
        kind={copy.kind}
        branding={branding}
      />
    );
  }

  if (phase === 'submitted') {
    return (
      <TerminalScreen
        kind="success"
        title="Thanks — your submission is being reviewed"
        message={`An administrator will email you at ${
          payload.contact.email || 'the address you provided'
        } once your submission has been approved.`}
        branding={branding}
      />
    );
  }

  if (phase === 'approved') {
    return (
      <TerminalScreen
        kind="success"
        title="You're approved"
        message="Check your email for your GreenCharge vehicle PIN — you can start charging right away."
        branding={branding}
      />
    );
  }

  return (
    <WizardChrome
      branding={branding}
      headerTitle={headerTitle}
      headerSubtitle={headerSubtitle}
      steps={STEPS}
      step={step}
      onBack={goBack}
      onNext={goNext}
      showNext={step < STEPS.length}
    >
      {currentStep.id === 'welcome' && (
        <StepWelcome
          welcomeTitle={welcomeTitle}
          intro={
            clientName
              ? `${clientName} is onboarding you to GreenCharge. This should take under a minute.`
              : undefined
          }
          expiresAt={link?.expiresAt}
        />
      )}
      {currentStep.id === 'contact' && (
        <StepContact payload={payload} updateField={updateField} />
      )}
      {currentStep.id === 'vehicle' && (
        <StepVehicle payload={payload} updateField={updateField} />
      )}
      {currentStep.id === 'review' && (
        <StepReview
          payload={payload}
          submitError={submitError}
          submitting={submitting}
          validationErrors={validationErrors}
          onSubmit={handleSubmit}
          onJumpToStep={jumpToStep}
          setAgreedToTerms={setAgreedToTerms}
        />
      )}
    </WizardChrome>
  );
}

export default SharedOnboardingFlow;
